<template>
  <el-dialog
    class="dialog-wrapper temperature-trail"
    visible
    show-close
    :close-on-click-modal="false"
    :append-to-body="appendToBody"
    @close="abortInsert"
  >
    <ContentHeader
      slot="title"
      :title="$t('sampleDetails.temperatureTrail.title')"
    />
    <ValidationObserver
      ref="temperatureTrailForm"
      v-slot="{ handleSubmit, invalid }"
    >
      <el-form
        ref="form"
        label-position="top"
      >
        <el-row
          :gutter="20"
          type="flex"
        >
          <el-col :span="8">
            <FormItem
              rules="required"
              :label="$t('sampleDetails.temperatureTrail.thermostat')"
            >
              <el-autocomplete
                v-model="selectedThermostat"
                style="width: 100%"
                :fetch-suggestions="thermostatsList"
                clearable
                value-key="label"
                placeholder=""
                @select="selectThermostat"
                @change="selectThermostat"
              >
                <template slot-scope="{ item }">
                  <AutocompleteItem :item="item" />
                </template>
              </el-autocomplete>
            </FormItem>
            <p v-if="formDataBulk.thermostatId">
              {{ $t('analysesData.valuesTemperatureBulk.thermostatTemp') }}
              {{ selectedThermostatTemp }} °C
            </p>
          </el-col>
          <el-col :span="8">
            <FormItem :label="$t('sampleDetails.temperatureTrail.startedAt')">
              <el-date-picker
                :disabled="formDataBulk.endedAt != null"
                v-model="formDataBulk.startedAt"
                format="dd-MM-yyyy HH:mm"
                value-format="yyyy-MM-dd HH:mm"
                type="datetime"
                placeholder=""
              />
            </FormItem>
          </el-col>
          <el-col :span="8">
            <FormItem :label="$t('sampleDetails.temperatureTrail.endedAt')">
              <el-date-picker
                :disabled="formDataBulk.startedAt != null"
                v-model="formDataBulk.endedAt"
                format="dd-MM-yyyy HH:mm"
                value-format="yyyy-MM-dd HH:mm"
                type="datetime"
                placeholder=""
              />
            </FormItem>
          </el-col>
        </el-row>

        <FormButtons>
          <el-button
            type="primary"
            native-type="submit"
            :disabled="
              invalid ||
              !formDataBulk.thermostatId ||
              formDataBulk.endedAt != null
            "
            @click.prevent="handleSubmit(createTemperatureTrail)"
          >
            {{ $t('analysesData.valuesTemperatureBulk.createTrace') }}
          </el-button>
          <el-button
            type="primary"
            native-type="submit"
            :disabled="formDataBulk.startedAt != null"
            @click.prevent="closeTemperatureTrail"
          >
            {{ $t('analysesData.valuesTemperatureBulk.closeTrace') }}
          </el-button>
        </FormButtons>
      </el-form>
    </ValidationObserver>

    <div class="table-wrapper temperature-trail__table">
      <el-table
        class="width-100"
        :data="thermostatTrail"
        border
      >
        <el-table-column
          prop="temperature"
          :label="$t('filters.temperature')"
        />
        <el-table-column
          prop="thermostat.name"
          :label="$t('filters.thermostat')"
        />
        <el-table-column :label="$t('filters.startedAt')">
          <template slot-scope="scope">
            {{ scope.row.startedAt | datetimeseconds }}
          </template>
        </el-table-column>
        <el-table-column :label="$t('filters.endedAt')">
          <template slot-scope="scope">
            {{ scope.row.endedAt | datetimeseconds }}
          </template>
        </el-table-column>
        <el-table-column
          :min-width="30"
          align="center"
        >
          <template slot-scope="scope">
            <span
              :style="{ cursor: 'pointer' }"
              @click="deleteTemperatureTrail(scope.row)"
            >
              <i class="state-icon fas fa-trash" />
            </span>
          </template>
        </el-table-column>
      </el-table>
    </div>
  </el-dialog>
</template>

<script>
  import moment from 'moment';

  import { thermostatsApi } from '@/api';
  import ContentHeader from '@/components/ContentHeader.vue';
  import FormButtons from '@/components/FormButtons.vue';
  import FormItem from '@/components/FormItem.vue';
  import AutocompleteItem from '@/components/AutocompleteItem.vue';

  const initialBulkData = {
    // temperature: null,
    startedAt: null,
    endedAt: null,
    thermostatId: null,
    // sampleAnalysisId
  };

  export default {
    name: 'CreateTemperatureTrail',
    components: {
      ContentHeader,
      FormButtons,
      FormItem,
      AutocompleteItem,
    },
    props: {
      labSampleAnalysisIds: { type: Number, required: true },
      appendToBody: { type: Boolean, default: false },
    },
    data() {
      return {
        formDataBulk: { ...initialBulkData },
        selectedThermostat: null,
        selectedThermostatTemp: null,
        thermostatTrail: [],
      };
    },
    created() {
      this.getTemperatureTrailForAnalysis();
    },
    methods: {
      async getTemperatureTrailForAnalysis() {
        const filter = {
          where: { labSampleAnalysisId: this.labSampleAnalysisIds },
        };
        try {
          const trailRemote = await thermostatsApi.getTemperatureTrail(filter);
          this.thermostatTrail = trailRemote.data;
        } catch (error) {
          this.$notify({
            showClose: true,
            type: 'error',
            message: error.response.data.error.message,
          });
        }
      },
      async createTemperatureTrail() {
        const data = {
          ...this.formDataBulk,
          labSampleAnalysisId: this.labSampleAnalysisIds,
          startedAt: this.formDataBulk.startedAt
            ? moment(this.formDataBulk.startedAt).toDate()
            : moment().toDate(),
          endedAt: this.formDataBulk.endedAt
            ? moment(this.formDataBulk.endedAt).toDate()
            : null,
        };
        try {
          await thermostatsApi.createTemperatureTrail(data);
          await this.getTemperatureTrailForAnalysis();
        } catch (error) {
          this.$notify({
            showClose: true,
            type: 'error',
            message: error.response.data.error.message,
          });
        }
      },
      async closeTemperatureTrail() {
        const data = {
          endedAt: this.formDataBulk.endedAt
            ? moment(this.formDataBulk.endedAt).toDate()
            : moment().toDate(),
        };
        try {
          await thermostatsApi.closeTemperatureTrail(
            this.labSampleAnalysisIds,
            data
          );
          await this.getTemperatureTrailForAnalysis();
        } catch (error) {
          this.$notify({
            showClose: true,
            type: 'error',
            message: error.response.data.error.message,
          });
        }
      },
      async deleteTemperatureTrail(row) {
        try {
          await thermostatsApi.deleteTemperatureTrail(row.id);
          await this.getTemperatureTrailForAnalysis();
        } catch (error) {
          this.$notify({
            showClose: true,
            type: 'error',
            message: error.response.data.error.message,
          });
        }
      },
      abortInsert() {
        this.$emit('dialogClose', { success: true });
      },
      async thermostatsList(value, cb) {
        const response = await thermostatsApi.autocompleteFetch(value);
        const thermostats = response.data.map((item) => ({
          label: item.name,
          value: item.id,
          enabled: item.enabled,
          currentTemperature: item.currentTemperature || null,
        }));
        cb(thermostats);
      },
      async selectThermostat(item = null) {
        this.selectedThermostatTemp = null;
        if (item && item.enabled) {
          if (!item.currentTemperature) {
            this.$notify({
              showClose: true,
              type: 'error',
              message: this.$t(
                'analysesData.valuesTemperatureBulk.noTemperatureErr'
              ),
            });
          } else {
            this.formDataBulk.thermostatId = item.value;
            this.selectedThermostatTemp = item.currentTemperature.value;
          }
        }
        if (item && !item.enabled) {
          this.$notify({
            showClose: true,
            type: 'error',
            message: this.$t(
              'analysesData.valuesTemperatureBulk.notEnabledErr'
            ),
          });
        }
        if (!item) {
          this.formDataBulk.thermostatId = null;
          this.selectedThermostat = null;
        }
      },
    },
  };
</script>

<style lang="scss">
  .temperature-trail {
    &__table {
      padding-top: 1rem;
    }
  }
</style>
